//== tab patterns
//== manages ARIA tab panels with keyboard navigation and optional URL hash deep-linking.

//== equalheights/AOS must re-run when tabs change so hidden panel content is measured correctly
import { applyEqualHeights } from './equalheights.js';
import { refreshAOS } from './anims-aos-init.js';

function initPanelTabs() {
  const tablists = document.querySelectorAll('[role="tablist"]');
  if (!tablists.length) return;

  tablists.forEach((tablist) => {
    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    const activateTab = (tab, { moveFocus = true } = {}) => {
      tabs.forEach((t) => {
        const selected = t === tab;

        t.setAttribute('aria-selected', selected);
        t.classList.toggle('is-active', selected);
        t.tabIndex = selected ? 0 : -1;

        const panelId = t.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        panel.toggleAttribute('hidden', !selected);
        panel.toggleAttribute('inert', !selected);
        panel.classList.toggle('is-active', selected);
      });

      //== update URL hash for deep-linkable tablists only
      const panelId = tab.getAttribute('aria-controls');
      if (panelId && tablist.hasAttribute('data-deep-link')) {
        history.replaceState(null, '', `#${panelId}`);
      }

      if (moveFocus) tab.focus();

      //== re-run equal heights and AOS as newly visible panel content may affect element sizes
      applyEqualHeights();
      refreshAOS();
    };

    //== initialise state
    tabs.forEach((tab) => {
      const selected = tab.getAttribute('aria-selected') === 'true';
      tab.tabIndex = selected ? 0 : -1;

      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      panel.toggleAttribute('hidden', !selected);
      panel.toggleAttribute('inert', !selected);
    });

    //== click handling
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab));
    });

    //== keyboard navigation
    tablist.addEventListener('keydown', (e) => {
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let newIndex;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          newIndex = (currentIndex + 1) % tabs.length;
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;

        case 'Home':
          newIndex = 0;
          break;

        case 'End':
          newIndex = tabs.length - 1;
          break;

        default:
          return;
      }

      e.preventDefault();
      activateTab(tabs[newIndex]);
    });

    //== open tab matching URL hash on page load (no focus jump)
    if (tablist.hasAttribute('data-deep-link')) {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const target = tabs.find((t) => t.getAttribute('aria-controls') === hash);
        if (target) activateTab(target, { moveFocus: false });
      }
    }
  });
}

//== re-activate on hash change (browser back/forward or on-page anchor links)
window.addEventListener('hashchange', () => {
  const hash = window.location.hash.slice(1);
  if (!hash) return;
  const tab = document.querySelector(`[role="tablist"][data-deep-link] [role="tab"][aria-controls="${hash}"]`);
  if (tab) tab.click();
});

initPanelTabs();
